import {
  BackButton,
  Button,
  Icon,
  ScopeBadge,
  Text,
  Notes,
  Reminders,
  Memories,
  ProfileSkeleton,
  DeleteModal,
  VoiceRecordButton,
  GenericSelectionModal,
} from '@components'
import { Screen } from '../../../components/templates/Screen'
import {
  RouteProp,
  useIsFocused,
  useNavigation,
  useRoute,
  useTheme,
} from '@react-navigation/native'
import React, { useEffect, useRef, useState } from 'react'
import { Animated, Linking, ScrollView, TouchableOpacity, View } from 'react-native'
import { ProfileImage } from '../../profile/components'
import { GhostActionButtons, About } from './components'

import { getCommonStyles, Scope, WEB_APP_URL } from '@utils'
import { AddContactModal } from '../addContacts/components'
import { FriendRequestModal } from '../../notifications/components'
import { defaultImage } from '@assets'
import {
  CalendarEvent,
  Profile,
  type ContactHistoryItem,
  useAnswerRequestedContacts,
  useCancelRequestedContacts,
  useCreationStackIntegration,
  useDeleteGhostProfile,
  useDeleteContact,
  useGetProfileById,
  isProfileMissingError,
  usePaginateCategories,
  usePaginateHashtags,
  usePendingContactRequests,
  useResolveLinkedinProfile,
  useScreen,
  useSendContactRequest,
  useSyncProfilePins,
  useUpdateContactScope,
  useUpdateGhostProfile,
  useUpdateGhostProfileScope,
} from '@hooks'
import { useCurrentUserStore } from '@store'
import { useLanguage } from '@context'
import { isSameArrays, SelectionItem } from '@utils'
import { getDetailProfileStyle } from './styles'
import AddGhostForm from '../addGhostContact/components/AddGhostForm'
import { useAddGhostContact } from '../addGhostContact/hooks'
import dayjs from 'dayjs'

export const DetailsGhostProfile: React.FC = () => {
  const theme = useTheme()
  const styles = getDetailProfileStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const { isMobile, isWeb } = useScreen()
  const { t } = useLanguage()
  const isFocused = useIsFocused()

  const route: any = useRoute()
  const { isCreationStackEnabled, showReminderCreation, showNoteCreation } =
    useCreationStackIntegration()

  // Check if we're in modal context
  const payload = route.params?.payload
  const isExtension = !!payload
  // In extension mode, the hash id is not a real KeepTouch id. Resolve first.
  const [profileId, setProfileId] = useState<string>(route.params?.profileId)
  const {
    data: profileData,
    isFetching,
    isPaused,
    error: profileError,
    refetch: refetchProfile,
  } = useGetProfileById(profileId)
  // A link to nobody, or a profile that could not be loaded at all: a plain
  // message instead of an empty page with live buttons. Not in the extension,
  // where the page is built from the payload.
  const isProfileMissing = !isExtension && !!profileId && isProfileMissingError(profileError)
  // In a browser without connection the request is not sent but left waiting
  // (paused), with no error: that counts as not loaded too.
  const isProfileUnavailable =
    !isExtension && !!profileId && (!!profileError || isPaused) && !profileData && !isProfileMissing
  const [profile, setProfile] = useState<Profile | undefined>()
  const [isAddContactModalVisible, setIsAddContactModalVisible] = useState(false)
  const [isRequestPendingLocal, setIsRequestPendingLocal] = useState(false)
  const [isCancelRequestVisible, setIsCancelRequestVisible] = useState(false)
  const [isAnswerRequestVisible, setIsAnswerRequestVisible] = useState(false)
  // « Voir » next to « N autres notes » in the note editor opens the Notes tab.
  const tabParam =
    useRoute<RouteProp<{ Details: { tab?: 'note' } | undefined }, 'Details'>>().params?.tab
  const [selectedTab, setSelectedTab] = useState<'about' | 'note' | 'reminder' | 'memory'>(
    tabParam === 'note' ? 'note' : 'about',
  )
  useEffect(() => {
    if (tabParam === 'note') setSelectedTab('note')
  }, [tabParam])
  const [showBackButton, setShowBackButton] = useState(true)
  const [scope, setScope] = useState<Scope>(profileData?.scope || Scope.PRIVATE)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)

  const { formData, handleFormChange, handleSave, isSaving } = useAddGhostContact(profile)

  const [isFloatingExpanded, setIsFloatingExpanded] = useState(true)
  const floatingAnim = useRef(new Animated.Value(1)).current
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const collapseFloating = () => {
    setIsFloatingExpanded(false)
    Animated.timing(floatingAnim, { toValue: 0, duration: 200, useNativeDriver: true }).start()
  }

  const expandFloating = () => {
    setIsFloatingExpanded(true)
    Animated.timing(floatingAnim, { toValue: 1, duration: 200, useNativeDriver: true }).start()
  }

  useEffect(() => {
    if (!isMobile || !profileId) return
    collapseTimer.current = setTimeout(collapseFloating, 3000)
    return () => {
      if (collapseTimer.current) clearTimeout(collapseTimer.current)
    }
  }, [isMobile, profileId])

  const [hashtagsSearchText, setHashtagsSearchText] = useState('')
  const [categoriesSearchText, setCategoriesSearchText] = useState('')
  const [isHashtagsModalVisible, setIsHashtagsModalVisible] = useState(false)
  const [isCategoriesModalVisible, setIsCategoriesModalVisible] = useState(false)
  const [selectedHashtags, setSelectedHashtags] = useState<SelectionItem[]>(
    profile?.pinsHashtags?.map((h) => ({ id: h.id, name: h.name })) || [],
  )
  const [selectedCategories, setSelectedCategories] = useState<SelectionItem[]>(
    profile?.pinsCategories?.map((c) => ({ id: c.id, name: c.name, photo: c.avatarUrl || '' })) ||
      [],
  )

  const {
    flatData: hashtags,
    isFetching: isFetchingHashtags,
    isFetchingNextPage: isFetchingNextPageHashtags,
    fetchNextPage: fetchNextPageHashtags,
    hasNextPage: hasNextPageHashtags,
  } = usePaginateHashtags(hashtagsSearchText)
  const {
    flatData: categories,
    isFetching: isFetchingCategories,
    isFetchingNextPage: isFetchingNextPageCategories,
    fetchNextPage: fetchNextPageCategories,
    hasNextPage: hasNextPageCategories,
  } = usePaginateCategories(categoriesSearchText)
  const {
    mutate: updateGhostProfile,
    isPending: isSavingGhostProfile,
    variables: ghostProfileBeingSaved,
  } = useUpdateGhostProfile()
  // A contact who has an account is not saved through the card: each hashtag
  // or category keeps its own list of people.
  const {
    mutate: syncHashtagPins,
    isPending: isSyncingHashtags,
    variables: hashtagPinsBeingSynced,
  } = useSyncProfilePins('hashtag')
  const {
    mutate: syncCategoryPins,
    isPending: isSyncingCategories,
    variables: categoryPinsBeingSynced,
  } = useSyncProfilePins('category')
  // Until a save has come back and the profile is read again, `profile` still
  // holds the old hashtags / categories: what is being saved is the reference.
  const hashtagIdsBeingSaved = isSavingGhostProfile
    ? ghostProfileBeingSaved?.pinsHashtags
    : isSyncingHashtags
      ? hashtagPinsBeingSynced?.nextIds
      : undefined
  const categoryIdsBeingSaved = isSavingGhostProfile
    ? ghostProfileBeingSaved?.pinsCategories
    : isSyncingCategories
      ? categoryPinsBeingSynced?.nextIds
      : undefined
  // The ticks sent with the save on its way: the ids alone cannot be shown.
  const hashtagsBeingSaved = useRef<SelectionItem[]>([])
  const categoriesBeingSaved = useRef<SelectionItem[]>([])
  const { mutate: updateGhostProfileScope } = useUpdateGhostProfileScope()

  const { mutateAsync: resolveLinkedinProfile } = useResolveLinkedinProfile()
  const { mutate: sendContactRequest } = useSendContactRequest()
  const { mutate: updateContactScope } = useUpdateContactScope()
  const { mutateAsync: deleteGhostProfile } = useDeleteGhostProfile()
  const { mutateAsync: deleteContact } = useDeleteContact()
  const { mutate: cancelContactRequest } = useCancelRequestedContacts()
  const { mutate: answerContactRequest } = useAnswerRequestedContacts()
  const { sentByRecipientId, receivedBySenderId } = usePendingContactRequests()
  const navigation = useNavigation()

  // A line of « Historique avec X » that is not a note: a message opens the
  // conversation; rendez-vous and reminders live in the « Rendez-vous /
  // Rappel » tab.
  const openHistoryItem = (item: ContactHistoryItem) => {
    const nav = navigation as any
    if (item.type === 'message' && item.conversationId) {
      nav.navigate('Messages', {
        screen: 'Conversation',
        params: { conversationId: item.conversationId },
      })
    } else {
      setSelectedTab('reminder')
    }
  }
  const { userID } = useCurrentUserStore()
  const isMe = profile?.accountId === userID

  useEffect(() => {
    const fetchProfile = async () => {
      if (profileId && profileData) {
        setProfile(profileData)
        if (profileData.scope) {
          setScope(profileData.scope)
        }
      } else {
        if (!payload) return
        try {
          const data = JSON.parse(atob(payload))
          const newProfile = {
            professions: [],
            fullName: data.name,
            // optimistic local parsing; final ID + names come from the API resolve
            firstName: data.name?.split(' ')?.[0],
            lastName: data.name?.split(' ')?.slice(1).join(' '),
            avatarUrl: data.image,
            gender: 'male',
            birthDate: dayjs().format('DD-MM-YYYY'),
            isGhost: true,
          }
          setProfile(newProfile as unknown as Profile)
          handleFormChange('birthDate', dayjs().format('DD-MM-YYYY'))
          // Resolve/Upsert in DB then switch to the canonical id.
          // Extension payloads include platform/handle/profileUrl so the API can
          // dedupe across social networks (linkedin/facebook/twitter/instagram).
          const resolved = await resolveLinkedinProfile({
            fullName: data.name,
            avatarUrl: data.image,
            bio: data.title || undefined,
            platform: data.platform,
            handle: data.handle,
            profileUrl: data.profileUrl,
          })

          if (resolved?.id) {
            if (resolved.kind === 'ghost') {
              handleFormChange('id', resolved.id)
            } else {
              setProfileId(resolved.id)
            }
          }
        } catch {
          // If not logged in, axios interceptor should try background login.
          // We keep the local extension profile in UI until retry succeeds.
        }
      }
    }
    fetchProfile()
  }, [profileId, profileData])

  // The ticks of the two pickers, put back to what is saved on the profile,
  // or to what was just sent while that save is still on its way: leaving the
  // screen or a profile refresh must not bring the old ticks back meanwhile.
  const resetSelectedHashtags = () =>
    setSelectedHashtags(
      hashtagIdsBeingSaved
        ? hashtagsBeingSaved.current
        : (profile?.pinsHashtags ?? []).map((h) => ({ id: h.id, name: h.name })),
    )
  const resetSelectedCategories = () =>
    setSelectedCategories(
      categoryIdsBeingSaved
        ? categoriesBeingSaved.current
        : (profile?.pinsCategories ?? []).map((c) => ({
            id: c.id,
            name: c.name,
            photo: c.avatarUrl || '',
          })),
    )

  useEffect(() => {
    if (profile?.pinsHashtags) {
      resetSelectedHashtags()
    }
    if (profile?.pinsCategories) {
      resetSelectedCategories()
    }
  }, [profile?.pinsHashtags, profile?.pinsCategories])

  useEffect(() => {
    if (!isFocused) {
      // This screen stays mounted while another one is on top of it. Emptying
      // the ticks here made the pickers open with nothing ticked on the way
      // back, and closing one then saved that empty list: every hashtag or
      // category of the person was removed. Unsaved ticks are dropped instead.
      resetSelectedHashtags()
      resetSelectedCategories()
      setIsHashtagsModalVisible(false)
      setIsCategoriesModalVisible(false)
    }
  }, [isFocused])

  const isGhost = !profileId || profile?.isGhost
  // A member who is not one of my contacts: the server only gives their name,
  // photo, bio and hashtags, so the rest of the page has nothing to show.
  const isStranger = !!profileId && !!profile && !isMe && !profile.isGhost && !profile.scope
  const isPendingFromServer = !!profileId && sentByRecipientId.has(profileId)
  const isRequestPending = (isRequestPendingLocal || isPendingFromServer) && !profile?.scope
  // My request still waiting (it can be cancelled), or theirs to me (the
  // button then answers it instead of sending a second one the other way).
  const sentRequestId = profileId ? sentByRecipientId.get(profileId) : undefined
  const receivedRequestId =
    profileId && !profile?.scope ? receivedBySenderId.get(profileId) : undefined

  const beforeSaving = () => {
    setProfileId(formData.id)
    handleSave()
  }

  const onScopeChange = (newScope: Scope) => {
    setScope(newScope)
    setProfile((prev) => (prev ? { ...prev, scope: newScope } : prev))
    if (!profileId) return
    if (isGhost) {
      updateGhostProfileScope({ ghostProfileId: profileId, scope: newScope })
    } else {
      updateContactScope({ contactId: profileId, scope: newScope })
    }
  }

  const onAddPress = () => {
    setIsAddContactModalVisible(true)
  }

  const handleAddPrivate = () => {
    sendContactRequest(
      {
        recipientId: profileId,
        scope: Scope.PRIVATE,
      },
      {
        onSuccess: () => {
          setIsRequestPendingLocal(true)
          setIsAddContactModalVisible(false)
        },
      },
    )
  }

  const handleCancelRequest = () => {
    if (!sentRequestId) return
    cancelContactRequest(
      { requestedContactId: sentRequestId },
      // Also when it fails: the waiting requests are read again, and a
      // request answered meanwhile must not leave « Demande envoyée » behind.
      { onSettled: () => setIsRequestPendingLocal(false) },
    )
  }

  const handleAnswerRequest = (answer: 'CONFIRM' | 'DECLINE', answerScope?: Scope) => {
    if (!receivedRequestId) return
    answerContactRequest({ requestedContactId: receivedRequestId, answer, scope: answerScope })
  }

  const handleAddProfessional = () => {
    sendContactRequest(
      {
        recipientId: profileId,
        scope: Scope.PROFESSIONAL,
      },
      {
        onSuccess: () => {
          setIsRequestPendingLocal(true)
          setIsAddContactModalVisible(false)
        },
      },
    )
  }

  const handleEventPress = (event: CalendarEvent) => {
    showReminderCreation({
      isCreationStack: true,
      eventId: event.id,
      contactId: profileId,
    })
  }

  const handleNotePress = (noteId: string) => {
    showNoteCreation({
      isCreationStack: true,
      noteId: noteId,
      contactId: profileId,
      isGhost: isGhost || !profile?.scope,
    })
  }

  const handleScroll = (event: any) => {
    const scrollY = event.nativeEvent.contentOffset.y
    setShowBackButton(scrollY <= 10) // Show back button when scrolled less than 10px from top
  }

  const openKeepTouch = () => {
    void Linking.openURL(`${WEB_APP_URL}/profile/${profileId || formData.id}`)
  }

  const handleDeleteConfirm = async () => {
    if (!profileId) return
    try {
      if (isGhost) {
        await deleteGhostProfile(profileId)
      } else {
        await deleteContact({ id: profileId })
      }
      setIsDeleteModalVisible(false)
      ;(navigation as any).goBack?.()
    } catch {
      // The hook shows the error; stay on the profile.
    }
  }

  return isFetching ? (
    <Screen>
      <ProfileSkeleton />
    </Screen>
  ) : isProfileMissing || isProfileUnavailable ? (
    <Screen>
      <BackButton title={t('common.back')} noOffset={!isWeb} />
      <View testID="profile-not-found" style={styles.notFound}>
        <Text type="titleBold" style={styles.notFoundText}>
          {t(isProfileMissing ? 'ghostProfile.notFoundTitle' : 'ghostProfile.loadErrorTitle')}
        </Text>
        <Text type="body" color={theme.colors.greyBlue} style={styles.notFoundText}>
          {t(isProfileMissing ? 'ghostProfile.notFoundMessage' : 'ghostProfile.loadErrorMessage')}
        </Text>
        {isProfileUnavailable && (
          <Button unfilled isSmall title={t('common.retry')} onPress={() => refetchProfile()} />
        )}
      </View>
    </Screen>
  ) : (
    <Screen>
      {showBackButton && !payload && !isCategoriesModalVisible && !isHashtagsModalVisible && (
        <BackButton title={t('common.back')} noOffset={!isWeb} />
      )}
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          styles.contentContainer,
          isMobile && !!profileId && !isMe && styles.contentContainerWithFloating,
          showBackButton && isWeb && commonStyles.paddingTop,
        ]}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {isExtension && (
          <TouchableOpacity
            disabled={!profileId}
            onPress={openKeepTouch}
            style={[
              commonStyles.row,
              commonStyles.alignSelfEnd,
              commonStyles.paddingHorizontal,
              commonStyles.paddingTop,
              commonStyles.paddingBottom,
            ]}
          >
            <Text type="bodySmall" color={theme.colors.primary}>
              {t('ghostProfile.openOnKeepTouch')}
            </Text>
            <Icon name="openInNew" size={theme.iconSize.small} color={theme.colors.primary} />
          </TouchableOpacity>
        )}
        <View style={styles.content}>
          <View style={styles.rightSection}>
            {profile?.scope && <ScopeBadge scope={[scope]} onChange={onScopeChange} />}
          </View>
          <View style={styles.infoContainer}>
            <ProfileImage profileId={profileId} isGhost={isGhost} avatarUrl={profile?.avatarUrl} />
            <View style={styles.infoTextContainer}>
              <View style={[styles.titleContainer, isExtension && styles.titleContainerExtension]}>
                <Text type="titleBold" style={styles.name}>
                  {profile?.fullName}
                </Text>
              </View>
              <Text type="body" color={theme.colors.primary} style={styles.work}>
                {profile?.contacts?.email ||
                  profile?.contacts?.phone ||
                  profile?.contacts?.website ||
                  profile?.contacts?.facebook ||
                  profile?.contacts?.instagram ||
                  profile?.contacts?.twitter ||
                  profile?.contacts?.linkedin}
              </Text>
              <View style={styles.row}>
                {/* No profession known (a stranger, or none filled in): no line,
                    rather than « Freelance » by default. */}
                {!isStranger && (profile?.professions?.length ?? 0) > 0 && (
                  <View style={styles.locationContainer}>
                    <Icon
                      name="work"
                      size={theme.iconSize.extraSmall}
                      color={theme.colors.greyBlue}
                    />
                    <Text type="body" color={theme.colors.greyBlue} style={styles.locationText}>
                      {profile?.professions?.map((p) => p.name).join(' & ')}
                    </Text>
                  </View>
                )}
                {profile?.residence && (
                  <View style={styles.locationContainer}>
                    <Icon
                      name="mapPin"
                      size={theme.iconSize.extraSmall}
                      color={theme.colors.greyBlue}
                    />
                    <Text type="body" color={theme.colors.greyBlue} style={styles.locationText}>
                      {profile?.residence?.name ?? profile?.residence?.nameFr}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </View>
          <GhostActionButtons
            isGhost={isGhost}
            isExtension={isExtension && !profileId}
            contact={profile!}
            isRequestPending={isRequestPending}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            onAddPress={onAddPress}
            hasReceivedRequest={!!receivedRequestId}
            onAnswerPress={() => setIsAnswerRequestVisible(true)}
            onCancelRequestPress={sentRequestId ? () => setIsCancelRequestVisible(true) : undefined}
            // A picker always opens on what is saved, even after a save that
            // failed, or on what was just sent while its save is on its way.
            // Someone who is not a contact yet cannot be put in a hashtag or a
            // category: the two buttons are not shown.
            onHashtagsPress={
              isStranger
                ? undefined
                : () => {
                    resetSelectedHashtags()
                    setIsHashtagsModalVisible(true)
                  }
            }
            onCategoriesPress={
              isStranger
                ? undefined
                : () => {
                    resetSelectedCategories()
                    setIsCategoriesModalVisible(true)
                  }
            }
          />
          <View style={!isMobile && styles.container}>
            {selectedTab === 'about' &&
              (!profileId ? (
                <AddGhostForm
                  formData={formData}
                  handleFormChange={handleFormChange}
                  handleSave={beforeSaving}
                  isSaving={isSaving}
                />
              ) : isStranger ? (
                // Instead of the titles Anniversaire, Lieux de vie… with nothing under them.
                <Text
                  type="body"
                  color={theme.colors.greyBlue}
                  style={isMobile ? [styles.strangerNotice, styles.strangerNoticeMobile] : styles.strangerNotice}
                >
                  {t(
                    receivedRequestId
                      ? 'ghostProfile.strangerNoticeReceived'
                      : isRequestPending
                        ? 'ghostProfile.strangerNoticePending'
                        : 'ghostProfile.strangerNotice',
                    { name: profile?.firstName },
                  )}
                </Text>
              ) : (
                <About profile={profile} />
              ))}
            {selectedTab === 'note' && (
              <Notes
                profileId={profileId}
                isGhost={isGhost || !profile?.scope}
                onNotePress={isCreationStackEnabled ? handleNotePress : undefined}
                insights={
                  profile
                    ? { firstName: profile.firstName, onHistoryPress: openHistoryItem }
                    : undefined
                }
              />
            )}
            {selectedTab === 'reminder' && (
              <Reminders
                selectedContactId={profileId}
                contactAccountId={profile?.accountId}
                birthDate={profile?.birthDate}
                isGhost={isGhost || !profile?.scope}
                withAddButton
                onEventPress={isCreationStackEnabled ? handleEventPress : undefined}
              />
            )}
            {selectedTab === 'memory' && (
              <Memories
                profileId={profileId}
                contactAccountId={profile?.accountId ?? (isGhost ? profileId : undefined)}
              />
            )}
          </View>
        </View>
      </ScrollView>
      {/* On one's own profile the panel would hold nothing. */}
      {isMobile && profileId && !isMe && (
        <View style={styles.floatingPanel}>
          <Animated.View
            pointerEvents={isFloatingExpanded ? 'auto' : 'none'}
            style={[
              styles.floatingContent,
              {
                opacity: floatingAnim,
                transform: [
                  {
                    translateY: floatingAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [12, 0],
                    }),
                  },
                ],
              },
            ]}
          >
            <VoiceRecordButton profileId={profileId} />
            {/* Someone who is not a contact cannot be removed from the contacts. */}
            {!isStranger && (
              <TouchableOpacity
                style={styles.floatingDeleteButton}
                onPress={() => setIsDeleteModalVisible(true)}
              >
                <Icon name="trash" size={theme.iconSize.small} color={theme.colors.error} />
              </TouchableOpacity>
            )}
          </Animated.View>
          <TouchableOpacity
            testID="profile-floating-toggle"
            style={styles.floatingToggle}
            onPress={isFloatingExpanded ? collapseFloating : expandFloating}
          >
            <Icon
              name={isFloatingExpanded ? 'arrowDown' : 'arrowUp'}
              size={theme.iconSize.small}
              color={theme.colors.secondary}
            />
          </TouchableOpacity>
        </View>
      )}

      {/* Only a card or a real contact can be removed: not oneself, not a stranger. */}
      {!isMobile && profileId && !isMe && !isStranger && (
        <View
          style={[
            commonStyles.alignSelfEnd,
            commonStyles.paddingTop,
            commonStyles.paddingBottom,
            commonStyles.paddingHorizontal,
          ]}
        >
          <TouchableOpacity onPress={() => setIsDeleteModalVisible(true)}>
            <Text type="bodySmall" color={theme.colors.error}>
              {isGhost ? t('ghostProfile.deleteProfile') : t('contacts.uncontact')}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {!isMobile && !isMe && profileId && (
        <View
          style={[
            commonStyles.absoluteBottom,
            commonStyles.alignStart,
            commonStyles.paddingTop,
            commonStyles.paddingBottom,
            commonStyles.paddingHorizontal,
          ]}
        >
          <VoiceRecordButton profileId={profileId} />
        </View>
      )}
      <AddContactModal
        isVisible={isAddContactModalVisible}
        setIsVisible={(visible) =>
          setIsAddContactModalVisible(
            typeof visible === 'function' ? visible(isAddContactModalVisible) : visible,
          )
        }
        contactName={profile?.fullName || ''}
        onAddPrivate={handleAddPrivate}
        onAddProfessional={handleAddProfessional}
      />
      <DeleteModal
        isVisible={isCancelRequestVisible}
        setIsVisible={setIsCancelRequestVisible}
        title=""
        heading={t('ghostProfile.cancelRequestHeading')}
        description={t('ghostProfile.cancelRequestTitle', { name: profile?.fullName || '' })}
        deleteLabel={t('ghostProfile.cancelRequest')}
        cancelLabel={t('common.back')}
        onDelete={handleCancelRequest}
      />
      <FriendRequestModal
        isVisible={isAnswerRequestVisible}
        setIsVisible={setIsAnswerRequestVisible}
        avatar={profile?.avatarUrl ? { uri: profile.avatarUrl } : defaultImage}
        name={profile?.fullName || ''}
        onAcceptPrivate={() => handleAnswerRequest('CONFIRM', Scope.PRIVATE)}
        onAcceptPro={() => handleAnswerRequest('CONFIRM', Scope.PROFESSIONAL)}
        onReject={() => handleAnswerRequest('DECLINE')}
      />
      <DeleteModal
        isVisible={isDeleteModalVisible}
        setIsVisible={setIsDeleteModalVisible}
        title={isGhost ? t('ghostProfile.deleteConfirmTitle') : t('contacts.uncontactConfirmTitle')}
        description={isGhost ? t('ghostProfile.deleteConfirmMessage') : undefined}
        onDelete={handleDeleteConfirm}
      />
      <GenericSelectionModal
        isVisible={isHashtagsModalVisible}
        setIsVisible={setIsHashtagsModalVisible}
        title={t('ghostProfile.hashtags')}
        placeholder={t('ghostProfile.searchHashtags')}
        availableItems={hashtags}
        selectedItems={selectedHashtags}
        onSelectionChange={setSelectedHashtags}
        onClosed={() => {
          const savedIds = hashtagIdsBeingSaved ?? (profile?.pinsHashtags ?? []).map((h) => h.id)
          const nextIds = selectedHashtags.map((h) => h.id)
          if (!profile || isSameArrays(savedIds, nextIds)) return
          if (profile.isGhost) {
            hashtagsBeingSaved.current = selectedHashtags
            updateGhostProfile({ id: profile.id, pinsHashtags: nextIds })
          } else if (profile.accountId && profile.scope) {
            hashtagsBeingSaved.current = selectedHashtags
            syncHashtagPins({
              profileId,
              accountId: profile.accountId,
              previousIds: savedIds,
              nextIds,
            })
          }
        }}
        isHashtagsModal
        isFetching={isFetchingHashtags}
        fetchNextPage={fetchNextPageHashtags}
        hasNextPage={hasNextPageHashtags}
        isFetchingNextPage={isFetchingNextPageHashtags}
        onSearch={setHashtagsSearchText}
        fitParent
      />
      <GenericSelectionModal
        isVisible={isCategoriesModalVisible}
        setIsVisible={setIsCategoriesModalVisible}
        title={t('ghostProfile.categories')}
        placeholder={t('ghostProfile.searchCategories')}
        availableItems={categories.map((c) => ({
          id: c.id,
          name: c.name,
          photo: c.avatarUrl || '',
        }))}
        selectedItems={selectedCategories}
        onSelectionChange={setSelectedCategories}
        onClosed={() => {
          const savedIds =
            categoryIdsBeingSaved ?? (profile?.pinsCategories ?? []).map((c) => c.id)
          const nextIds = selectedCategories.map((c) => c.id)
          if (!profile || isSameArrays(savedIds, nextIds)) return
          if (profile.isGhost) {
            categoriesBeingSaved.current = selectedCategories
            updateGhostProfile({ id: profile.id, pinsCategories: nextIds })
          } else if (profile.accountId && profile.scope) {
            categoriesBeingSaved.current = selectedCategories
            syncCategoryPins({
              profileId,
              accountId: profile.accountId,
              previousIds: savedIds,
              nextIds,
            })
          }
        }}
        isCategoriesModal
        isFetching={isFetchingCategories}
        fetchNextPage={fetchNextPageCategories}
        hasNextPage={hasNextPageCategories}
        isFetchingNextPage={isFetchingNextPageCategories}
        onSearch={setCategoriesSearchText}
        fitParent
      />
    </Screen>
  )
}
